import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import coreLogoMark from '../../assets/Logodl.svg';
import iconLogoMark from '../../assets/LogoIcon.svg';
import { HudPath, hudModules, hudPaths, toHudHash } from '../content';
import { DataRing, RadialCore, ScannerOverlay, SystemFrame } from '../primitives';

type LaunchBlueprint = {
  path: HudPath;
  angle: number;
  radius: number;
  vector: string;
  state: string;
  accent: 'lime' | 'aqua';
};

type LaunchNode = LaunchBlueprint & {
  id: string;
  shortLabel: string;
};

let hasPlayedStartupSequence = false;
const startupSequenceStorageKey = 'dl_home_startup_played';

const getStartupSequenceHistory = (): boolean => {
  if (hasPlayedStartupSequence) {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage.getItem(startupSequenceStorageKey) === '1';
  } catch {
    return false;
  }
};

const markStartupSequencePlayed = () => {
  hasPlayedStartupSequence = true;

  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(startupSequenceStorageKey, '1');
  } catch {
    // Ignore storage failures and keep in-memory fallback.
  }
};

const launchBlueprints: readonly LaunchBlueprint[] = [
  { path: hudPaths.serviceArchitecture, angle: -96, radius: 46, vector: 'VECTOR 01', state: 'READY', accent: 'lime' },
  { path: hudPaths.automationSystems, angle: -24, radius: 42, vector: 'VECTOR 02', state: 'PRIMED', accent: 'aqua' },
  { path: hudPaths.workflowPipeline, angle: 34, radius: 42, vector: 'VECTOR 03', state: 'READY', accent: 'lime' },
  { path: hudPaths.verifiedOperations, angle: 96, radius: 43, vector: 'VECTOR 04', state: 'VERIFIED', accent: 'aqua' },
  { path: hudPaths.communicationsTerminal, angle: 180, radius: 41, vector: 'VECTOR 05', state: 'OPEN', accent: 'lime' }
];

const telemetryLeft = [
  { label: 'SIGNAL QUALITY', value: 96 },
  { label: 'SYSTEM STABILITY', value: 94 },
  { label: 'COMMAND AVAILABILITY', value: 98 },
  { label: 'ROUTE INTEGRITY', value: 92 }
] as const;

const telemetryRight = [
  { label: 'ROUTING STATUS', value: 92 },
  { label: 'SUBSYSTEM SYNC', value: 90 },
  { label: 'CONTROL BUS', value: 95 },
  { label: 'INTERFACE LATENCY', value: 88 }
] as const;

const statusStrip = [
  { label: 'ROUTE MATRIX', value: 'SYNCED' },
  { label: 'AUTH LAYER', value: 'VERIFIED' },
  { label: 'TELEMETRY BUS', value: 'STREAMING' },
  { label: 'NAV LINKS', value: 'READY' },
  { label: 'CORE LOCK', value: 'STABLE' },
  { label: 'SENSOR SWEEP', value: 'ACTIVE' }
] as const;

const polarToPercent = (angleDeg: number, radiusPercent: number) => {
  const radians = (angleDeg * Math.PI) / 180;
  return {
    x: 50 + Math.cos(radians) * radiusPercent,
    y: 50 + Math.sin(radians) * radiusPercent
  };
};

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(value, max));

const getViewport = () => {
  if (typeof window === 'undefined') {
    return { width: 1440, height: 900 };
  }

  return { width: window.innerWidth, height: window.innerHeight };
};

const TypedHudText = ({
  text,
  active,
  delay = 0,
  charInterval = 18,
  enabled = true,
  className
}: {
  text: string;
  active: boolean;
  delay?: number;
  charInterval?: number;
  enabled?: boolean;
  className?: string;
}) => {
  const reducedMotion = useReducedMotion();
  const shouldType = enabled && !reducedMotion;
  const [visibleText, setVisibleText] = useState(() => (active && !shouldType ? text : ''));

  useEffect(() => {
    if (!active) {
      setVisibleText(shouldType ? '' : text);
      return;
    }

    if (!shouldType) {
      setVisibleText(text);
      return;
    }

    setVisibleText('');
    let currentIndex = 0;
    let interval = 0;
    const timeout = window.setTimeout(() => {
      interval = window.setInterval(() => {
        currentIndex += 1;
        setVisibleText(text.slice(0, currentIndex));
        if (currentIndex >= text.length) {
          window.clearInterval(interval);
        }
      }, charInterval);
    }, delay * 1000);

    return () => {
      window.clearTimeout(timeout);
      if (interval) {
        window.clearInterval(interval);
      }
    };
  }, [active, charInterval, delay, shouldType, text]);

  const completed = visibleText.length >= text.length;

  return (
    <span className={className}>
      {visibleText}
      {shouldType && active && !completed ? (
        <motion.span
          className="ml-0.5 inline-block h-[0.82em] w-[0.08rem] bg-current align-[-0.16em]"
          animate={{ opacity: [0.15, 1, 0.15] }}
          transition={{ duration: 0.7, repeat: Infinity }}
        />
      ) : null}
    </span>
  );
};

const CountUpPercent = ({
  value,
  active,
  delay = 0,
  duration = 1,
  animate = true,
  className
}: {
  value: number;
  active: boolean;
  delay?: number;
  duration?: number;
  animate?: boolean;
  className?: string;
}) => {
  const reducedMotion = useReducedMotion();
  const shouldAnimate = animate && !reducedMotion;
  const [displayValue, setDisplayValue] = useState(active && !shouldAnimate ? value : 0);

  useEffect(() => {
    if (!active) {
      setDisplayValue(0);
      return;
    }

    if (!shouldAnimate) {
      setDisplayValue(value);
      return;
    }

    let raf = 0;
    let startedAt = 0;
    const timeout = window.setTimeout(() => {
      const tick = (timestamp: number) => {
        if (!startedAt) {
          startedAt = timestamp;
        }

        const progress = Math.min((timestamp - startedAt) / (duration * 1000), 1);
        setDisplayValue(Math.round(value * progress));

        if (progress < 1) {
          raf = window.requestAnimationFrame(tick);
        }
      };

      raf = window.requestAnimationFrame(tick);
    }, delay * 1000);

    return () => {
      window.clearTimeout(timeout);
      if (raf) {
        window.cancelAnimationFrame(raf);
      }
    };
  }, [active, delay, duration, shouldAnimate, value]);

  return <span className={className}>{displayValue}%</span>;
};

const TelemetryRail = ({
  title,
  tone,
  metrics,
  reducedMotion,
  active,
  delayBase = 0,
  animateCounters = true,
  countDuration = 1.35,
  barDuration = 1.2
}: {
  title: string;
  tone: 'lime' | 'aqua';
  metrics: readonly { label: string; value: number }[];
  reducedMotion: boolean;
  active: boolean;
  delayBase?: number;
  animateCounters?: boolean;
  countDuration?: number;
  barDuration?: number;
}) => {
  const accent = tone === 'lime' ? 'text-brand-lime border-brand-lime/35' : 'text-brand-aqua border-brand-aqua/35';
  const glow = tone === 'lime' ? 'from-brand-lime/70 to-brand-lime/10' : 'from-brand-aqua/70 to-brand-aqua/10';

  return (
    <aside className={`pointer-events-none w-[13rem] rounded-lg border bg-brand-panel/82 p-3 ${accent}`}>
      <p className={`hud-micro ${tone === 'lime' ? 'text-brand-lime' : 'text-brand-aqua'}`}>{title}</p>
      <div className="mt-3 space-y-2.5">
        {metrics.map((metric, index) => {
          const entryDelay = delayBase + index * 0.14;
          return (
            <div key={metric.label}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[0.6rem] uppercase tracking-[0.16em] text-brand-text-3">{metric.label}</span>
                <CountUpPercent
                  value={metric.value}
                  active={active}
                  delay={entryDelay}
                  duration={countDuration}
                  animate={animateCounters}
                  className={`font-mono text-[0.64rem] ${tone === 'lime' ? 'text-brand-lime' : 'text-brand-aqua'}`}
                />
              </div>
              <div className="mt-1.5 h-[2px] overflow-hidden rounded-full bg-brand-panel-2">
                <motion.div
                  className={`h-full bg-gradient-to-r ${glow}`}
                  initial={false}
                  animate={{ width: active ? `${metric.value}%` : '0%' }}
                  transition={{ duration: barDuration, delay: entryDelay, ease: [0.2, 1, 0.32, 1] }}
                />
              </div>
              <motion.div
                className={`mt-1 h-[1px] bg-gradient-to-r ${glow}`}
                animate={reducedMotion ? undefined : { opacity: [0.2, 0.95, 0.25] }}
                transition={{ duration: 1.8, repeat: Infinity, delay: index * 0.15 }}
              />
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export const StartPanelPage = ({ activateStartup = true }: { activateStartup?: boolean }) => {
  const reducedMotion = useReducedMotion();
  const [viewport, setViewport] = useState(getViewport);
  const [startupHadHistory] = useState(getStartupSequenceHistory);
  const [startupArmed, setStartupArmed] = useState(reducedMotion || !activateStartup || startupHadHistory);
  const subsystemNodes: LaunchNode[] = launchBlueprints.flatMap((blueprint) => {
    const module = hudModules.find((candidate) => candidate.path === blueprint.path);
    if (!module) {
      return [];
    }

    return [{ ...blueprint, id: module.id, shortLabel: module.shortLabel }];
  });

  useEffect(() => {
    const updateViewport = () => {
      setViewport(getViewport());
    };

    updateViewport();
    window.addEventListener('resize', updateViewport, { passive: true });

    return () => {
      window.removeEventListener('resize', updateViewport);
    };
  }, []);

  useEffect(() => {
    if (!activateStartup) {
      setStartupArmed(false);
      return;
    }

    if (reducedMotion) {
      setStartupArmed(true);
      markStartupSequencePlayed();
      return;
    }

    if (startupHadHistory) {
      setStartupArmed(true);
      return;
    }

    setStartupArmed(false);
    const timeout = window.setTimeout(() => {
      setStartupArmed(true);
      markStartupSequencePlayed();
    }, 220);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [activateStartup, reducedMotion, startupHadHistory]);

  const commandCoreSize = useMemo(() => {
    const sideReserve = viewport.width >= 1536 ? 520 : viewport.width >= 1280 ? 110 : 54;
    const topReserve = viewport.height >= 860 ? 222 : 194;
    const bottomReserve = viewport.height >= 860 ? 184 : 166;
    const availableWidth = viewport.width - sideReserve;
    const availableHeight = viewport.height - (topReserve + bottomReserve);
    const targetSize = Math.min(availableWidth, availableHeight, 800);

    return clamp(Math.floor(targetSize), 320, 800);
  }, [viewport.height, viewport.width]);

  const subsystemNodeWidth = useMemo(() => clamp(Math.round(commandCoreSize * 0.15), 92, 118), [commandCoreSize]);
  const centerInsigniaSize = useMemo(() => clamp(Math.round(commandCoreSize * 0.285), 128, 170), [commandCoreSize]);
  const compactTelemetry = viewport.height < 780;
  const startupVisible = reducedMotion || startupArmed;
  const typedBootEnabled = !startupHadHistory;
  const metricBootEnabled = !startupHadHistory;
  const cinematicBoot = typedBootEnabled && !reducedMotion;

  return (
    <SystemFrame id="start-panel" className="min-h-[calc(100vh-5.5rem)] pt-3 pb-3">
      <section className="relative isolate min-h-[calc(100vh-6.2rem)] overflow-hidden rounded-[1.55rem] border border-brand-lime/30 bg-[radial-gradient(circle_at_50%_50%,rgba(6,20,22,0.44),rgba(2,6,10,0.96)_58%)]">
        <ScannerOverlay tone="intense" className="opacity-65" />
        <div className="pointer-events-none absolute inset-0 hud-grid opacity-24" />
        <motion.div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(139,255,62,0.2),transparent_62%)]"
          animate={reducedMotion ? undefined : { opacity: [0.45, 1, 0.45] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
        />
        {cinematicBoot ? (
          <motion.div
            className="pointer-events-none absolute inset-y-0 left-[-36%] w-[34%] bg-[linear-gradient(90deg,transparent,rgba(183,255,106,0.24),rgba(88,247,182,0.16),transparent)] blur-sm"
            initial={{ x: '0%', opacity: 0 }}
            animate={startupVisible ? { x: '520%', opacity: [0, 0.42, 0.24, 0] } : { x: '0%', opacity: 0 }}
            transition={{ duration: 2.8, delay: 0.55, ease: [0.2, 1, 0.32, 1] }}
          />
        ) : null}
        <div className="pointer-events-none absolute inset-0 hud-vignette opacity-85" />

        <motion.div
          className="absolute left-4 top-4 z-30 w-[min(95vw,30rem)] sm:left-5 sm:top-5"
          initial={false}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="relative overflow-hidden rounded-lg border border-brand-lime/35 bg-brand-panel/88 px-3 py-2.5">
            <motion.div
              className="pointer-events-none absolute inset-y-0 left-[-28%] w-[24%] bg-[linear-gradient(90deg,transparent,rgba(183,255,106,0.18),transparent)]"
              animate={reducedMotion ? undefined : { x: ['-4%', '520%'], opacity: [0.09, 0.46, 0.11] }}
              transition={{ duration: 5.2, repeat: Infinity, ease: 'linear' }}
            />

            <div className="flex items-center gap-3">
              <div
                data-logo-slot="develogic-insignia"
                className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-md border border-brand-lime/45 bg-brand-panel-2"
              >
                <img src={iconLogoMark} alt="DeveLogic Digital logo" className="h-8 w-8 object-contain" />
              </div>
              <div className="min-w-0">
                <p className="hud-micro text-brand-lime">COMMAND INSIGNIA</p>
                <TypedHudText
                  text="DEVELOGIC DIGITAL // ROOT"
                  active={startupVisible}
                  enabled={typedBootEnabled}
                  delay={1.48}
                  charInterval={22}
                  className="block truncate font-display text-[0.8rem] tracking-[0.16em] text-brand-text"
                />
                <p className="hud-micro text-brand-text-3">SWAP READY ASSET SLOT</p>
              </div>
              <div className="hidden sm:block">
                <div className="flex items-center gap-1 rounded-md border border-brand-aqua/35 bg-brand-panel-2/70 px-2 py-1">
                  <motion.span
                    className="h-1.5 w-1.5 rounded-full bg-brand-aqua"
                    animate={reducedMotion ? undefined : { opacity: [0.35, 1, 0.35], scale: [0.8, 1.2, 0.8] }}
                    transition={{ duration: 1.6, repeat: Infinity }}
                  />
                  <span className="font-mono text-[0.58rem] uppercase tracking-[0.2em] text-brand-text-2">DL-OS</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="pointer-events-none absolute right-4 top-5 z-30 hidden items-center gap-3 rounded-md border border-brand-aqua/35 bg-brand-panel/84 px-3 py-2 md:flex"
          initial={false}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="hud-micro text-brand-aqua">ROOT STATUS</span>
          <span className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-brand-lime">NOMINAL</span>
          <span className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-brand-aqua">SIGNAL HIGH</span>
        </motion.div>

        <motion.div
          className="pointer-events-none absolute left-5 top-1/2 z-20 hidden -translate-y-1/2 2xl:block"
          initial={false}
          animate={{ opacity: 1, x: 0 }}
        >
          <TelemetryRail
            title="INSTRUMENTATION A"
            tone="lime"
            metrics={telemetryLeft}
            reducedMotion={reducedMotion}
            active={startupVisible}
            delayBase={2.92}
            animateCounters={metricBootEnabled}
            countDuration={1.55}
            barDuration={1.35}
          />
        </motion.div>

        <motion.div
          className="pointer-events-none absolute right-5 top-1/2 z-20 hidden -translate-y-1/2 2xl:block"
          initial={false}
          animate={{ opacity: 1, x: 0 }}
        >
          <TelemetryRail
            title="INSTRUMENTATION B"
            tone="aqua"
            metrics={telemetryRight}
            reducedMotion={reducedMotion}
            active={startupVisible}
            delayBase={3.08}
            animateCounters={metricBootEnabled}
            countDuration={1.55}
            barDuration={1.35}
          />
        </motion.div>

        <div className="relative z-20 flex min-h-[calc(100vh-9.2rem)] items-center justify-center px-3 pb-20 pt-20">
          <motion.div
            className="relative aspect-square w-full max-w-[50rem]"
            style={{ width: `${commandCoreSize}px` }}
            initial={false}
            animate={{ opacity: 1, scale: 1 }}
          >
            <svg className="pointer-events-none absolute inset-[4%] z-10" viewBox="0 0 100 100" aria-hidden="true">
              {subsystemNodes.map((node) => {
                const point = polarToPercent(node.angle, node.radius);
                return (
                  <g key={`link-${node.id}`}>
                    <motion.path
                      d={`M 50 50 L ${point.x} ${point.y}`}
                      fill="none"
                      stroke={node.accent === 'lime' ? 'rgba(183,255,106,0.65)' : 'rgba(88,247,182,0.65)'}
                      strokeWidth="0.35"
                      strokeDasharray="2 3"
                      animate={reducedMotion ? undefined : { strokeDashoffset: [0, -30], opacity: [0.45, 1, 0.45] }}
                      transition={{ duration: 2.6, repeat: Infinity, ease: 'linear' }}
                    />
                    <motion.circle
                      cx={point.x}
                      cy={point.y}
                      r="1.2"
                      fill={node.accent === 'lime' ? 'rgba(183,255,106,0.95)' : 'rgba(88,247,182,0.95)'}
                      animate={reducedMotion ? undefined : { opacity: [0.4, 1, 0.4], scale: [0.75, 1.18, 0.75] }}
                      transition={{ duration: 1.6, repeat: Infinity }}
                    />
                  </g>
                );
              })}
              <line x1="50" y1="8" x2="50" y2="92" stroke="rgba(183,255,106,0.12)" strokeWidth="0.2" />
              <line x1="8" y1="50" x2="92" y2="50" stroke="rgba(88,247,182,0.12)" strokeWidth="0.2" />
            </svg>

            <motion.div
              className="pointer-events-none absolute inset-[2%] rounded-full border border-brand-lime/32"
              animate={reducedMotion ? undefined : { rotate: 360 }}
              transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
            />
            <motion.div
              className="pointer-events-none absolute inset-[7%] rounded-full border border-brand-aqua/30 border-dashed"
              animate={reducedMotion ? undefined : { rotate: -360 }}
              transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
            />
            <motion.div
              className="pointer-events-none absolute inset-[12%] rounded-full border border-brand-lime/28"
              animate={reducedMotion ? undefined : { opacity: [0.2, 0.86, 0.2] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="pointer-events-none absolute inset-[17%] rounded-full border border-brand-aqua/24 border-dashed"
              animate={reducedMotion ? undefined : { rotate: 360, opacity: [0.16, 0.45, 0.16] }}
              transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
            />
            <div className="absolute inset-[21%] overflow-hidden rounded-full border border-brand-lime/38 bg-brand-panel/72 shadow-[0_0_60px_rgba(183,255,106,0.16)]">
              <RadialCore />
              <motion.div
                className="pointer-events-none absolute inset-0 bg-[conic-gradient(from_48deg_at_50%_50%,transparent_0%,rgba(183,255,106,0.3)_16%,transparent_35%,transparent_100%)] mix-blend-screen"
                animate={reducedMotion ? undefined : { rotate: 360 }}
                transition={{ duration: 7.2, repeat: Infinity, ease: 'linear' }}
              />
              <motion.div
                className="pointer-events-none absolute inset-0 bg-[conic-gradient(from_218deg_at_50%_50%,transparent_0%,rgba(88,247,182,0.2)_17%,transparent_42%,transparent_100%)] mix-blend-screen"
                animate={reducedMotion ? undefined : { rotate: -360 }}
                transition={{ duration: 9, repeat: Infinity, ease: 'linear' }}
              />
            </div>

            <motion.div
              className="pointer-events-none absolute left-1/2 top-[53%] z-20 -translate-x-1/2 -translate-y-1/2 text-center"
              initial={false}
              animate={{ opacity: 1, scale: 1, y: 0 }}
            >
              <motion.div
                className="mx-auto grid place-items-center rounded-full border border-brand-lime/45 bg-brand-panel/88 p-3 shadow-[0_0_28px_rgba(183,255,106,0.24)]"
                style={{ width: `${centerInsigniaSize}px`, height: `${centerInsigniaSize}px` }}
                animate={reducedMotion ? undefined : { boxShadow: ['0 0 20px rgba(183,255,106,0.18)', '0 0 34px rgba(183,255,106,0.35)', '0 0 20px rgba(183,255,106,0.18)'] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
              >
                <img
                  src={coreLogoMark}
                  alt="DeveLogic Digital central insignia"
                  className="h-[250%] w-[250%] object-contain drop-shadow-[0_0_12px_rgba(183,255,106,0.52)]"
                />
              </motion.div>
              <div className="mt-2 rounded-full border border-brand-lime/35 bg-brand-panel/82 px-3 py-1">
                <TypedHudText
                  text="CENTRAL COMMAND CORE"
                  active={startupVisible}
                  enabled={typedBootEnabled}
                  delay={2.56}
                  charInterval={20}
                  className="hud-micro text-brand-lime"
                />
                <p className="hud-micro text-brand-text-3">SYSTEM ACTIVE / ROUTE READY</p>
              </div>
            </motion.div>

            <div className="pointer-events-none absolute left-1/2 top-[14%] z-20 -translate-x-1/2 text-center">
              <p className="hud-micro text-brand-aqua">ROOT SCREEN / INTERFACE FIRST</p>
              {compactTelemetry ? null : (
                <TypedHudText
                  text="Advanced software systems, AI operations, and verified delivery controls."
                  active={startupVisible}
                  enabled={typedBootEnabled}
                  delay={2.24}
                  charInterval={18}
                  className="mt-1 block text-[0.68rem] uppercase tracking-[0.14em] text-brand-text-2"
                />
              )}
            </div>

            {subsystemNodes.map((node, index) => {
              const point = polarToPercent(node.angle, node.radius);
              return (
                <motion.a
                  key={node.id}
                  href={toHudHash(node.path)}
                  className={`group absolute z-30 -translate-x-1/2 -translate-y-1/2 rounded-md border bg-brand-panel/92 p-1.5 text-left backdrop-blur-sm sm:p-2 ${
                    node.accent === 'lime' ? 'border-brand-lime/55 hover:border-brand-lime' : 'border-brand-aqua/55 hover:border-brand-aqua'
                  }`}
                  style={{ left: `${point.x}%`, top: `${point.y}%`, width: `${subsystemNodeWidth}px` }}
                  initial={false}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  whileHover={reducedMotion ? undefined : { scale: 1.04 }}
                >
                  <p className={`hud-micro ${node.accent === 'lime' ? 'text-brand-lime' : 'text-brand-aqua'}`}>{node.vector}</p>
                  <TypedHudText
                    text={node.shortLabel}
                    active={startupVisible}
                    enabled={typedBootEnabled}
                    delay={2.66 + index * 0.14}
                    charInterval={17}
                    className="mt-1 block text-[0.58rem] font-semibold uppercase tracking-[0.14em] text-brand-text sm:text-[0.64rem]"
                  />
                  <p className="hud-micro mt-1 text-brand-text-3">{node.state}</p>
                </motion.a>
              );
            })}
          </motion.div>
        </div>

        <div className="pointer-events-none absolute bottom-[4.8rem] left-6 z-20 hidden xl:block">
          <DataRing value={96} label="Signal" size={84} bootSequence active={startupVisible} startDelay={3.45} countDuration={1.7} />
        </div>
        <div className="pointer-events-none absolute bottom-[4.8rem] right-6 z-20 hidden xl:block">
          <DataRing
            value={94}
            label="Stability"
            size={84}
            ringColor="var(--color-brand-aqua)"
            bootSequence
            active={startupVisible}
            startDelay={3.6}
            countDuration={1.75}
          />
        </div>

        <div className="absolute bottom-3 left-3 right-3 z-30 border border-brand-lime/22 bg-brand-panel/78 px-2 py-2">
          <div className="grid gap-1.5 sm:grid-cols-3 lg:grid-cols-6">
            {statusStrip.map((item) => (
              <motion.div
                key={item.label}
                className="flex items-center justify-between border border-brand-lime/14 bg-brand-panel-2/45 px-2 py-1"
                initial={false}
                animate={{ opacity: 1, y: 0 }}
              >
                <span className="font-mono text-[0.55rem] uppercase tracking-[0.14em] text-brand-text-3">{item.label}</span>
                <span className="font-mono text-[0.58rem] uppercase tracking-[0.18em] text-brand-lime">{item.value}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </SystemFrame>
  );
};
